import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { toast } from 'sonner';
import CasinoSidebar from '@/components/CasinoSidebar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Gamepad2, ChevronRight, Check, Mail, Share2, TrendingUp, AlertTriangle, ArrowRight } from 'lucide-react';

function CountUp({ value, duration = 800 }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const startTime = performance.now();
    const animate = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(Math.round(eased * value * 100) / 100);
      if (progress < 1) ref.current = requestAnimationFrame(animate);
    };
    ref.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(ref.current);
  }, [value, duration]);
  return <>{display.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>;
}

const GAME_DEFAULTS = {
  'Slots': { spins: 600, rtp: 96.0 },
  'Blackjack': { spins: 80, rtp: 99.5 },
  'Roulette': { spins: 40, rtp: 97.3 },
  'Baccarat': { spins: 80, rtp: 98.9 },
  'Video Poker': { spins: 60, rtp: 99.5 },
};

export default function Cost2PlayCalculator() {
  const [gameType, setGameType] = useState('Slots');
  const [betSize, setBetSize] = useState('');
  const [spinsPerHour, setSpinsPerHour] = useState('600');
  const [duration, setDuration] = useState('');
  const [rtp, setRtp] = useState('96');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const defaults = GAME_DEFAULTS[gameType];
    if (defaults) {
      setSpinsPerHour(String(defaults.spins));
      setRtp(String(defaults.rtp));
    }
  }, [gameType]);

  const handleCalculate = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/tools/calculate-cost', {
        game_type: gameType, bet_size: parseFloat(betSize),
        spins_per_hour: parseInt(spinsPerHour), duration: parseFloat(duration), rtp: parseFloat(rtp),
      });
      setResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Calculation failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-[1280px] mx-auto px-4 lg:px-8 py-8 lg:py-12" data-testid="cost2play-calculator-page">
      <nav className="flex items-center gap-2 text-sm text-[#64748b] mb-6" data-testid="breadcrumb">
        <Link to="/dashboard" className="hover:text-[#4d7cfe] transition-colors">Dashboard</Link>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-[#a0a8b8]">Play Smart</span>
        <ChevronRight className="h-3.5 w-3.5" />
        <span className="text-white">Cost2Play Calculator</span>
      </nav>

      <div className="flex items-start gap-4 mb-8 animate-fade-in">
        <div className="w-12 h-12 rounded-xl bg-[#8b5cf6]/15 flex items-center justify-center shrink-0">
          <Gamepad2 className="h-6 w-6 text-[#8b5cf6]" />
        </div>
        <div>
          <h1 className="text-2xl lg:text-[32px] font-bold text-white" style={{ letterSpacing: '-0.005em' }}>Cost2Play Calculator</h1>
          <p className="text-[#a0a8b8] text-sm mt-1">See exactly how much each game costs you over time based on house edge and RTP</p>
          <div className="flex items-center gap-1.5 mt-2 text-xs text-[#64748b]"><TrendingUp className="h-3 w-3" /> 8,920 uses this month</div>
        </div>
      </div>

      <div className="flex flex-col lg:flex-row gap-8">
        <div className="flex-1 min-w-0">
          <div className="flex flex-col lg:flex-row gap-6">
            <div className="lg:w-[55%]">
              <div className="ciq-card">
                <form onSubmit={handleCalculate} className="space-y-5">
                  <div>
                    <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Game Type</label>
                    <Select value={gameType} onValueChange={setGameType}>
                      <SelectTrigger className="h-11 bg-[#1a1f2e] border-white/10 text-white" data-testid="cost-game-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#252b3b] border-white/10">
                        {Object.keys(GAME_DEFAULTS).map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Average Bet Size</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#64748b] text-sm">$</span>
                      <input type="number" value={betSize} onChange={(e) => setBetSize(e.target.value)} required min="0.01" step="0.01"
                        className="w-full h-11 pl-8 pr-4 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none"
                        placeholder="1.00" data-testid="cost-bet-size-input" />
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Spins/Hands Per Hour</label>
                    <input type="number" value={spinsPerHour} onChange={(e) => setSpinsPerHour(e.target.value)} required min="1"
                      className="w-full h-11 px-4 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none"
                      data-testid="cost-spins-input" />
                    <p className="text-[10px] text-[#64748b] mt-1">Typical: Slots 600/hr, Blackjack 80/hr, Roulette 40/hr</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Play Duration (hours)</label>
                    <input type="number" value={duration} onChange={(e) => setDuration(e.target.value)} required min="0.5" step="0.5"
                      className="w-full h-11 px-4 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none"
                      placeholder="2" data-testid="cost-duration-input" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#a0a8b8] mb-1.5 block">Game RTP (Return to Player %)</label>
                    <div className="relative">
                      <input type="number" value={rtp} onChange={(e) => setRtp(e.target.value)} required min="80" max="100" step="0.01"
                        className="w-full h-11 px-4 pr-8 rounded-lg bg-[#1a1f2e] border border-white/10 text-white text-sm ciq-input focus:outline-none"
                        data-testid="cost-rtp-input" />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[#64748b] text-sm">%</span>
                    </div>
                    <p className="text-[10px] text-[#64748b] mt-1">Don't know? <Link to="/play-smart/rtp-comparison-engine" className="text-[#4d7cfe] hover:underline">Use RTP Comparison Engine</Link></p>
                  </div>
                  <button type="submit" disabled={loading}
                    className="w-full h-12 rounded-xl bg-[#8b5cf6] text-white font-medium text-sm btn-lift disabled:opacity-50"
                    data-testid="cost-calculate-btn">
                    {loading ? 'Calculating...' : 'Calculate Cost'}
                  </button>
                </form>
              </div>
            </div>

            <div className="lg:w-[45%]">
              {result && (
                <div className="animate-slide-up space-y-4">
                  <div className="ciq-card border-[#f59e0b]/30 bg-[#f59e0b]/5">
                    <div className="flex items-center gap-2 mb-1">
                      <AlertTriangle className="h-4 w-4 text-[#f59e0b]" />
                      <p className="text-sm text-[#a0a8b8]">Expected Cost to Play</p>
                    </div>
                    <div className="text-3xl font-bold text-[#f59e0b]" data-testid="cost-result-amount">
                      $<CountUp value={result.expected_cost} />
                    </div>
                    <p className="text-xs text-[#64748b] mt-1">for {result.duration_hours} hours of play</p>
                  </div>

                  <div className="ciq-card">
                    <h3 className="text-white font-semibold text-sm mb-3">Breakdown</h3>
                    <div className="space-y-2.5">
                      <div className="flex justify-between text-sm"><span className="text-[#a0a8b8]">Total Wagered</span><span className="text-white">${result.total_wagered.toLocaleString()}</span></div>
                      <div className="flex justify-between text-sm"><span className="text-[#a0a8b8]">House Edge</span><span className="text-[#f59e0b]">{result.house_edge_pct}%</span></div>
                      <div className="flex justify-between text-sm bg-[#ef4444]/10 px-3 py-2 rounded-lg -mx-1">
                        <span className="text-[#ef4444] font-medium">Expected Loss</span>
                        <span className="text-[#ef4444] font-bold" data-testid="cost-expected-loss">${result.expected_cost.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>

                  <div className="ciq-card">
                    <h3 className="text-white font-semibold text-sm mb-3">Cost Comparison by Duration</h3>
                    <div className="space-y-1.5">
                      {result.comparisons.map((c, i) => (
                        <div key={i} className={`flex justify-between text-sm px-3 py-2 rounded-lg ${c.is_selected ? 'bg-[#4d7cfe]/10 border border-[#4d7cfe]/20' : ''}`}>
                          <span className={c.is_selected ? 'text-[#4d7cfe] font-medium' : 'text-[#a0a8b8]'}>{c.duration}</span>
                          <span className={c.is_selected ? 'text-[#4d7cfe] font-bold' : 'text-white'}>${c.cost.toLocaleString()}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="ciq-card">
                    <h3 className="text-white font-semibold text-sm mb-3">Cost Reduction Tips</h3>
                    <ul className="space-y-2.5">
                      {result.tips.map((tip, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-[#a0a8b8]">
                          <Check className="h-4 w-4 text-[#10b981] shrink-0 mt-0.5" /> {tip}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex gap-3">
                    <button onClick={() => toast.info('Email feature coming soon!')} className="flex-1 h-10 rounded-lg border border-white/10 text-[#a0a8b8] text-sm hover:bg-white/5 transition-colors flex items-center justify-center gap-2" data-testid="cost-email-btn">
                      <Mail className="h-3.5 w-3.5" /> Email Results
                    </button>
                    <button onClick={() => toast.info('Share link copied!')} className="flex-1 h-10 rounded-lg border border-white/10 text-[#a0a8b8] text-sm hover:bg-white/5 transition-colors flex items-center justify-center gap-2" data-testid="cost-share-btn">
                      <Share2 className="h-3.5 w-3.5" /> Share
                    </button>
                  </div>
                </div>
              )}

              {!result && (
                <div className="ciq-card flex flex-col items-center justify-center py-12 text-center">
                  <Gamepad2 className="h-10 w-10 text-[#3a4155] mb-3" />
                  <p className="text-[#64748b] text-sm">Configure your game settings to see the expected cost of play</p>
                </div>
              )}
            </div>
          </div>

          {result && (
            <div className="ciq-card mt-6 animate-fade-in" style={{ animationDelay: '300ms' }}>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-black flex items-center justify-center shrink-0 p-1">
                  <img src="https://customer-assets.emergentagent.com/job_smart-player-hub/artifacts/cnni6myb_logo-light.png" alt="BitStarz" className="h-full object-contain" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white font-medium mb-1">Best RTP for {result.game_type} at BitStarz</p>
                  <p className="text-xs text-[#a0a8b8] mb-3">With 96.8% average RTP across 7,000+ games, get the best value for your play. Lightning-fast crypto withdrawals when you win.</p>
                  <a href={`https://bzstarz1.com/bhi8kvogt?utm_source=casinoiq&utm_medium=cost2play-calculator&utm_campaign=smart-player-tools`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-[#8b5cf6] text-white text-sm font-medium btn-lift"
                    data-testid="cost-bitstarz-cta"
                    onClick={async () => { try { await api.post('/affiliate/click', { casino_id: 'bitstarz', source_page: 'cost2play-calculator' }); } catch {} }}>
                    Play at BitStarz <ArrowRight className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            </div>
          )}
        </div>

        <CasinoSidebar sourcePage="cost2play-calculator" />
      </div>
    </div>
  );
}
